import type { IconName } from '@sbo/icons-vue';
import { storeToRefs } from 'pinia';
import { computed, watchEffect } from 'vue';
import { TOURNAMENT_CENTER_BASE_PATH, TOURNAMENT_CENTER_MATCHES_PATH } from '@/components/tournamentCenter/config';
import { useState } from '@/composable/useState';
import { NonNull } from '@/core/lib/array';
import { invertCapitalize } from '@/core/lib/string';
import type { IHotlink } from '@/interface/IHotlink';
import type { ISportListIcon } from '@/interface/ISport';
import {
    AsiaBetPageType, AsiaRoutePage, EventDateType, MarketPage, MultipleSportsZone, PlayerType, PromotionZone, PromotionZoneLayout, PromotionZoneType,
    SiteStyle, SportType,
} from '@/interface/enum';
import { useCustomerStore } from '@/store/customerStore';
import { useHotLinkStore } from '@/store/hotLinkStore';
import { useSportLeaguePageStore } from '@/store/sportLeaguePageStore';
import { useToggleStore } from '@/store/toggleStore';

type IIconSize = 'sm' | 'xs' | 'md' | 'lg' | 'xl' | 'xxl' | number;

export type IIcon =
    | { type: 'svg', name: string, size: IIconSize }
    | { type: 'sboIcon', name: IconName, size: IIconSize };

// Every hotlink lives under this prefix: Rogue's "/epl" is served at "/promo/epl".
export const HOTLINK_ROUTE_PREFIX = '/promo';

// GTM names a hotlink click by joining page, type and event with the label, which gives
// K_Lobby_HotLink_click[<en title>]. Only the "[<en title>]" part is ours to send: repeating the
// prefix in the label doubles it. The whole name must fit in 40 characters, so the title is cut
// to whatever is left after the parts GTM adds.
const HOTLINK_GTM_EVENT_NAME_PREFIX = 'K_Lobby_HotLink_click';
const HOTLINK_GTM_EVENT_NAME_MAX_LENGTH = 40;
const HOTLINK_GTM_TITLE_MAX_LENGTH = HOTLINK_GTM_EVENT_NAME_MAX_LENGTH - HOTLINK_GTM_EVENT_NAME_PREFIX.length - '[]'.length;

function buildHotlinkGtmLabel(title: string) {
    return `[${title.slice(0, HOTLINK_GTM_TITLE_MAX_LENGTH)}]`;
}

// Rogue's route becomes the single ":slug" segment of the hotlink route, so it must be one plain segment.
const HOTLINK_ROUTE_PATTERN = /^\/[\w-]+$/;

export interface IPromotionConfig {
    /**
     * The i18n key of title of the promotion zone.
     * For a hotlink zone this is the title itself, already resolved for the current locale — do not pass it to i18n.t.
     */
    name: string;
    /**
     * The route that should be navigated to when clicking the promotion zone
     */
    route: string;
    /**
     * The base path used for registering the router structure.
     * Use this when the router's base path differs from the hotlink navigation target (e.g. B2B deep-links).
     * Falls back to `route` if not provided.
     */
    routerBasePath?: string;
    routePage: AsiaRoutePage | null;
    enabled: boolean;
    /**
     * The icon that should be displayed in the promotion zone title
     */
    icon: IIcon | null;
    hotLink: {
        /**
         * The icon that should be displayed in the hot link
         */
        icon: ISportListIcon | null;
    } | null;
    /**
     * The sport type that should be selected by default when entering the promotion zone
     * If null, the sport type will not be changed
    */
    defaultSport?: SportType | null;
    /**
     * The league names filter for the promotion zone
     */
    filteredLeagueNames: string[] | null;
    /**
     * The league ids filter for the promotion zone
     */
    filteredLeagueIds: number[] | null;
    leagueIdsBySportType?: Partial<Record<SportType, number[]>>;
    defaultBetPage: AsiaBetPageType | null;
    gtmLabel: string;
    excludeMarketPages: MarketPage[];
    routeLayout: PromotionZoneLayout;
    type: PromotionZoneType;
    supportSports?: SportType[];
    /**
     * Which multi-sport event this zone currently runs.
     * The sports list hides that event's own sport type while the zone is enabled.
     */
    multipleSportsZone?: MultipleSportsZone;
}

/*
    Only used by useAsiaSportMarketInfoStore
 */
export const _usePromotionZone = () => {
    const [promotionZone, _setPromotionZone] = useState(PromotionZone.None);
    const [activeHotlinkRoute, _setActiveHotlinkRoute] = useState<string | null>(null);

    const setPromotionZone = (zone: PromotionZone, hotlinkRoute: string | null = null) => {
        _setPromotionZone(zone);
        _setActiveHotlinkRoute(zone === PromotionZone.Hotlink ? hotlinkRoute : null);
    };

    const { isEFootballEnabled } = storeToRefs(useCustomerStore());
    const {
        isShowMultipleSportsHotLink,
        multipleSportsLeagueNames,
        multipleSportsZoneSupportSports,
        eFootballZoneLeagueIds,
        simulationSportsZoneLeagueIds,
        simulationSportsZoneSupportedSports,
        tournamentCenterLeagueNames,
        isTournamentCenterEnabled,
        isSwitchEFootballToSimulationSportsZoneEnabled,
    } = storeToRefs(useToggleStore(SiteStyle.Asia));

    const { hotlinks } = storeToRefs(useHotLinkStore());

    const { sportType, leagueIds, leagueNames } = storeToRefs(useSportLeaguePageStore());

    const multipleSportsZoneEventDateOrder = [
        EventDateType.Today,
        EventDateType.Plus1,
        EventDateType.Plus2,
        EventDateType.Plus3,
        EventDateType.Plus4,
        EventDateType.Plus5,
        EventDateType.Plus6Over,
        EventDateType.All,
    ];

    const { playerType } = storeToRefs(useCustomerStore());

    function buildHotlinkZoneConfig(hotlink: IHotlink | undefined): IPromotionConfig | null {
        if (hotlink === undefined || hotlink.route === '') return null;
        if (!HOTLINK_ROUTE_PATTERN.test(hotlink.route)) {
            console.warn(`[usePromotionZone] invalid hotlink route ignored: ${hotlink.route}`);
            return null;
        }
        if (hotlink.id === '' || hotlink.title.trim() === '') {
            console.warn(`[usePromotionZone] hotlink without id or title ignored: ${hotlink.route}`);
            return null;
        }

        const sboIconName = getSboIconName(hotlink.sportType);
        if (!sboIconName) return null;

        return {
            name: hotlink.title,
            route: `${HOTLINK_ROUTE_PREFIX}${hotlink.route}`,
            routePage: AsiaRoutePage.SportsCupPage,
            enabled: true,
            icon: { name: sboIconName, size: 'lg', type: 'sboIcon' },
            hotLink: {
                icon: { name: sboIconName, size: 'lg', type: 'sboIcon' },
            },
            defaultSport: hotlink.sportType,
            filteredLeagueNames: hotlink.leagueNames.map(name => name.trim()).filter(Boolean),
            filteredLeagueIds: [],
            defaultBetPage: AsiaBetPageType.SportsCup,
            gtmLabel: buildHotlinkGtmLabel(hotlink.titleEn),
            excludeMarketPages: [],
            routeLayout: PromotionZoneLayout.Default,
            type: PromotionZoneType.Single,
            supportSports: [hotlink.sportType],
        };
    }

    const SBO_ICON_NAME_OVERRIDES: Partial<Record<SportType, IconName>> = {
        [SportType.Others]: 'sports:othrsColor',
        [SportType.Esports]: 'sports:eSportsColor',
    };

    function getSboIconName(sportType: SportType) {
        if (!isSportType(sportType)) return null;

        const override = SBO_ICON_NAME_OVERRIDES[sportType];
        if (override) return override;

        const sportName = invertCapitalize(SportType[sportType]);
        return `sports:${sportName}Color` as IconName;
    }

    function isSportType(value: unknown): value is SportType {
        return Object.values(SportType).includes(value as SportType);
    }

    const hotlinkZoneConfigs = computed<IPromotionConfig[]>(() => hotlinks.value
        .map(buildHotlinkZoneConfig)
        .filter(NonNull));

    const promotionZoneConfigs = computed<Record<PromotionZone, IPromotionConfig | null>>(() => ({
        [PromotionZone.None]: null,
        [PromotionZone.MultipleSports]: {
            name: `promotion_zone_${PromotionZone.MultipleSports}`,
            route: '/multiple-sports',
            routePage: AsiaRoutePage.MultipleSportsPage,
            enabled: isShowMultipleSportsHotLink.value,
            icon: { name: 'icon-sport-color-others', size: 'lg', type: 'svg' },
            hotLink: {
                icon: { name: 'icon-sport-color-others', size: 'lg', type: 'svg' },
            },
            defaultSport: SportType.Soccer,
            filteredLeagueNames: multipleSportsLeagueNames.value,
            filteredLeagueIds: [],
            defaultBetPage: AsiaBetPageType.MultipleSports,
            gtmLabel: 'Asia_Game',
            excludeMarketPages: [],
            routeLayout: PromotionZoneLayout.Default,
            type: PromotionZoneType.Multiple,
            supportSports: multipleSportsZoneSupportSports.value,
            multipleSportsZone: MultipleSportsZone.AsianGames,
        },
        [PromotionZone.EFootball]: {
            name: `promotion_zone_${PromotionZone.EFootball}`,
            route: '/e-football',
            routePage: AsiaRoutePage.EFootballPage,
            enabled: !isSwitchEFootballToSimulationSportsZoneEnabled.value && isEFootballEnabled.value,
            icon: { name: 'icon-system-e-football', size: 'lg', type: 'svg' },
            hotLink: {
                icon: { name: 'icon-system-e-football', size: 'lg', type: 'svg' },
            },
            defaultSport: SportType.Soccer,
            filteredLeagueNames: [],
            filteredLeagueIds: eFootballZoneLeagueIds.value,
            defaultBetPage: AsiaBetPageType.EFootball,
            gtmLabel: 'eFootball',
            excludeMarketPages: [MarketPage.Outright],
            routeLayout: PromotionZoneLayout.Default,
            type: PromotionZoneType.Single,
        },
        [PromotionZone.SimulationSports]: {
            name: `promotion_zone_${PromotionZone.SimulationSports}`,
            route: '/cyber-sports',
            routePage: AsiaRoutePage.SimulationSportsPage,
            enabled: isSwitchEFootballToSimulationSportsZoneEnabled.value && isEFootballEnabled.value,
            icon: { name: 'icon-sport-color-cyberSports', size: 'lg', type: 'svg' },
            hotLink: {
                icon: { name: 'icon-sport-color-cyberSports', size: 'lg', type: 'svg' },
            },
            filteredLeagueNames: [],
            filteredLeagueIds: Object.values(simulationSportsZoneLeagueIds.value).flat(),
            leagueIdsBySportType: simulationSportsZoneLeagueIds.value,
            defaultBetPage: AsiaBetPageType.SimulationSports,
            gtmLabel: 'CyberSports',
            excludeMarketPages: [MarketPage.Outright],
            routeLayout: PromotionZoneLayout.Default,
            type: PromotionZoneType.Multiple,
            supportSports: simulationSportsZoneSupportedSports.value,
        },
        [PromotionZone.Hotlink]: null,
        [PromotionZone.SportLeague]: {
            name: `promotion_zone_${PromotionZone.SportLeague}`,
            route: '/:sportRouteName',
            routePage: AsiaRoutePage.SportLeaguePage,
            enabled: true,
            icon: { name: 'icon-system-fire', size: 'lg', type: 'svg' },
            hotLink: null,
            defaultSport: sportType.value,
            filteredLeagueNames: leagueNames.value,
            filteredLeagueIds: leagueIds.value,
            defaultBetPage: AsiaBetPageType.SportLeague,
            gtmLabel: 'SportLeague',
            excludeMarketPages: [],
            routeLayout: PromotionZoneLayout.Default,
            type: PromotionZoneType.Single,
        },
        [PromotionZone.TournamentCenter]: {
            name: `promotion_zone_${PromotionZone.TournamentCenter}`,
            route: playerType.value === PlayerType.B2B ? TOURNAMENT_CENTER_MATCHES_PATH : TOURNAMENT_CENTER_BASE_PATH,
            routerBasePath: TOURNAMENT_CENTER_BASE_PATH,
            routePage: null,
            enabled: isTournamentCenterEnabled.value,
            icon: null,
            hotLink: {
                icon: {
                    name: 'icon-sport-color-worldCupColor',
                    size: 18,
                    style: {
                        margin: '0 -2px 0 0',
                    },
                    type: 'svg',
                },
            },
            defaultSport: SportType.Soccer,
            filteredLeagueNames: tournamentCenterLeagueNames.value,
            filteredLeagueIds: [],
            defaultBetPage: AsiaBetPageType.TournamentCenterMatches,
            gtmLabel: 'tournament-center',
            excludeMarketPages: [],
            routeLayout: PromotionZoneLayout.TournamentCenter,
            type: PromotionZoneType.Single,
        },
    }));

    /**
     * Known Bug: If two PromotionZones share identical `defaultSport`, `filteredLeagueNames`,
     * and `filteredLeagueIds`, the zone-switching logic will malfunction and the wrong zone
     * may become active.
     *
     * This should never happen under normal business configurations, but if it does
     * (e.g. misconfigured toggles), this computed will surface the conflicting pair
     * and a console.warn will be emitted to alert developers.
     */
    const conflictingPromotionZones = computed(() => {
        const sortedStringify = (arr: unknown[] | null) => JSON.stringify([...(arr ?? [])].sort((a, b) => String(a).localeCompare(String(b))));

        const configs = Object.entries(promotionZoneConfigs.value)
            .filter((entry): entry is [string, IPromotionConfig] => !!entry[1]?.enabled);

        for (let i = 0; i < configs.length; i++) {
            for (let j = i + 1; j < configs.length; j++) {
                const [keyA, a] = configs[i];
                const [keyB, b] = configs[j];

                const sameSport = a.defaultSport === b.defaultSport;
                const sameLeagueNames = sortedStringify(a.filteredLeagueNames)
                    === sortedStringify(b.filteredLeagueNames);
                const sameLeagueIds = sortedStringify(a.filteredLeagueIds)
                    === sortedStringify(b.filteredLeagueIds);

                if (sameSport && sameLeagueNames && sameLeagueIds) {
                    return { keyA, keyB, nameA: a.name, nameB: b.name };
                }
            }
        }
        return null;
    });

    watchEffect(() => {
        const conflict = conflictingPromotionZones.value;
        if (conflict && !import.meta.env.PROD) {
            // eslint-disable-next-line no-alert
            alert(
                '[usePromotionZone] Known Bug: Two PromotionZones share the same defaultSport, filteredLeagueNames, and filteredLeagueIds.\n'
                + 'This will cause incorrect zone-switching behavior — the system cannot distinguish between them.\n'
                + 'Business logic should prevent this from happening. Please check your toggle/config settings.\n'
                + `Zone A: ${conflict.keyA} (${conflict.nameA})\n`
                + `Zone B: ${conflict.keyB} (${conflict.nameB})`,
            );
        }
    });

    const getPromotionZoneConfig = (zone: PromotionZone, hotlinkRoute: string | null = null) => {
        if (zone === PromotionZone.Hotlink) {
            return hotlinkZoneConfigs.value.find(config => config.route === hotlinkRoute) ?? null;
        }
        return promotionZoneConfigs.value[zone] ?? null;
    };
    const currentPromotionZoneConfig = computed(() => getPromotionZoneConfig(promotionZone.value, activeHotlinkRoute.value));

    return {
        promotionZone,
        setPromotionZone,
        getPromotionZoneConfig,
        hotlinkZoneConfigs,
        currentPromotionZoneConfig,
        multipleSportsZoneEventDateOrder,
    };
};
